import { useCallback, useMemo } from 'react';

import orderBy from 'lodash.orderby';
import type { UseFormReturn } from 'react-hook-form';

import type { CompanyQueryLinkerWithPivotSchema } from '@/_schemas/companies/company-query-linker-with-pivot-schema';
import { showFormField } from '@/_shared';

import type { CompanyFormSchema } from '../schemas';

type UseSubsidiariesParams = {
  form: UseFormReturn<CompanyFormSchema>;
  isEdit: boolean;
};

export function useSubsidiaries({ form, isEdit }: UseSubsidiariesParams) {
  const [subsidiaries, primary_subsidiaries] = form.watch(['subsidiaries', 'primary_subsidiaries']);

  const parentIds = useMemo(() => new Set((primary_subsidiaries ?? []).map((item) => item.id)), [primary_subsidiaries]);

  const combinedSubsidiaries = useMemo<CompanyQueryLinkerWithPivotSchema[]>(() => {
    const parents = orderBy(primary_subsidiaries ?? [], 'name').map((item) => ({
      ...item,
      pivot: { type: 'primary' as const },
    }));
    const children = orderBy(
      (subsidiaries ?? []).filter((item) => !parentIds.has(item.id)),
      'name'
    );
    return [...parents, ...children];
  }, [primary_subsidiaries, subsidiaries, parentIds]);

  const showSubsidiaries = showFormField(isEdit, combinedSubsidiaries.length);

  const handleSubsidiariesChange = useCallback(
    (values: CompanyQueryLinkerWithPivotSchema[]) => {
      form.setValue(
        'subsidiaries',
        values.filter((item) => !parentIds.has(item.id)),
        { shouldDirty: true }
      );
    },
    [parentIds, form]
  );

  return {
    combinedSubsidiaries,
    showSubsidiaries,
    handleSubsidiariesChange,
  };
}
